import { MOCK_METRICS, MockGrain, MockMetric } from "./LocationSummaryV2MockData";
import { AsnMetricColumn } from "../constants/asnOnePagerConstants";

export const MOBILE_TOOLBAR_BREAKPOINT = 640;

export const ALL_GRAINS: readonly MockGrain[] = [
  "region",
  "market",
  "location",
  "locationType",
  "year",
  "period",
  "week",
] as const;

export const PAGE_SIZES = [25, 50, 100] as const;

export interface ColumnSectionOption {
  key: string;
  label: string;
}

export interface ColumnSection {
  label: string;
  options: readonly ColumnSectionOption[];
}

export const COLUMN_SECTIONS: readonly ColumnSection[] = [
  {
    label: "Location Level",
    options: [
      { key: "region", label: "Region / Unit" },
      { key: "market", label: "Market / POD" },
      { key: "location", label: "Location" },
      { key: "locationType", label: "Location Type" },
    ],
  },
  {
    label: "Time Period",
    options: [
      { key: "year", label: "Year" },
      { key: "period", label: "Period" },
      { key: "week", label: "Week" },
    ],
  },
  {
    label: "Metrics",
    options: MOCK_METRICS.map((metric) => ({
      key: metric.key,
      label: metric.label,
    })),
  },
];

export interface SortState {
  key: string;
  desc: boolean;
}

export interface ColumnWidths {
  grain: number;
  metric: number;
  table: number;
}

export const MIN_COL_WIDTHS = {
  grain: 165,
  metric: 130,
} as const;

export const COL_WEIGHTS = {
  grain: 165,
  metric: 130,
} as const;

export const getGrainLabel = (grain: MockGrain): string =>
  ({
    region: "Region / Unit",
    market: "Market / POD",
    location: "Location",
    locationType: "Location Type",
    year: "Year",
    period: "Period",
    week: "Week",
  }[grain]);

export const computeColumnWidths = (
  grainCount: number,
  metricCount: number,
  viewportWidth: number
): ColumnWidths => {
  const minTableWidth =
    grainCount * MIN_COL_WIDTHS.grain + metricCount * MIN_COL_WIDTHS.metric;
  const effectiveViewportWidth = Math.max(0, viewportWidth);

  if (effectiveViewportWidth > 0 && effectiveViewportWidth >= minTableWidth) {
    const totalWeight =
      grainCount * COL_WEIGHTS.grain + metricCount * COL_WEIGHTS.metric;
    const unit = totalWeight > 0 ? effectiveViewportWidth / totalWeight : 0;
    return {
      grain: unit * COL_WEIGHTS.grain,
      metric: unit * COL_WEIGHTS.metric,
      table: effectiveViewportWidth,
    };
  }

  return {
    grain: MIN_COL_WIDTHS.grain,
    metric: MIN_COL_WIDTHS.metric,
    table: minTableWidth,
  };
};

// ─── Export: map the table's live metric-column config to the Excel column shape ──
// The Excel export must show exactly the columns currently visible in the table —
// same keys, labels, and order as MOCK_METRICS filtered by the Columns toggle —
// never a separate hardcoded list. AsnMetricColumn's id/fmt tokens differ from this
// table's MockMetric key/format strings, so each entry is translated 1:1 below.
const METRIC_EXPORT_ID: Record<MockMetric, AsnMetricColumn["id"]> = {
  nonAsnLoads: "nonAsnLoads",
  totalLoads: "totalLoads",
  pctNonAsn: "pctNonAsn",
  pyPct: "pyPct",
  avgSkus: "avgSkus",
  caseQty: "caseQuantity",
};

const METRIC_EXPORT_FORMAT: Record<"int" | "pct" | "decimal", AsnMetricColumn["fmt"]> = {
  int: "int",
  pct: "pct2",
  decimal: "num1",
};

export const buildVisibleMetricExportColumns = (
  visibleMetrics: ReadonlyArray<{ key: MockMetric; label: string; format: "int" | "pct" | "decimal" }>
): AsnMetricColumn[] =>
  visibleMetrics.map((metric) => ({
    id: METRIC_EXPORT_ID[metric.key],
    label: metric.label,
    fmt: METRIC_EXPORT_FORMAT[metric.format],
  }));
